import React, { useEffect, useMemo, useState } from 'react'
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material'
import { IPermissionScope } from '../../../../../shared/types/api/user.type'

const ALLOWED_SCOPE_KEYS = ['f200', 'f201', 'f202', 'f203', 'f204', 'f205', 'f206', 'f207', 'f208', 'f302', 'f910']
type ScopeKey = (typeof ALLOWED_SCOPE_KEYS)[number]

interface Props {
  scope: IPermissionScope | null | undefined
  onChange: (next: IPermissionScope) => void
  allowedKeys?: ScopeKey[]
  labelKey?: string
  labelValue?: string
}

export const ScopeFieldEditor: React.FC<Props> = ({
  scope,
  onChange,
  allowedKeys = ALLOWED_SCOPE_KEYS as ScopeKey[],
  labelKey = 'Scope key',
  labelValue = 'Value',
}) => {
  // normalize incoming scope to a single key/value
  const firstEntry = useMemo(() => {
    if (!scope || typeof scope !== 'object') return undefined
    for (const k of ALLOWED_SCOPE_KEYS) {
      const v = (scope as any)[k]
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        return [k as ScopeKey, String(v)] as const
      }
    }
    return undefined
  }, [scope])

  const [keySel, setKeySel] = useState<ScopeKey | ''>(firstEntry?.[0] ?? '')
  const [val, setVal] = useState<string>(firstEntry?.[1] ?? '')

  // sync if parent changes (e.g., switching permissions)
  useEffect(() => {
    setKeySel((firstEntry?.[0] ?? '') as ScopeKey | '')
    setVal(firstEntry?.[1] ?? '')
  }, [firstEntry?.[0], firstEntry?.[1]])

  // emit normalized scope object with at most one key. Empty key → unscoped.
  const emit = (k: ScopeKey | '', value: string) => {
    if (!k) {
      onChange({} as IPermissionScope)
      return
    }
    onChange({ [k]: value } as IPermissionScope)
  }

  const onKeyChange = (e: SelectChangeEvent) => {
    const k = e.target.value as ScopeKey | ''
    setKeySel(k)
    emit(k, val)
  }

  const onValueChange = (v: string) => {
    setVal(v)
    emit(keySel, v)
  }

  const keyOptions = allowedKeys.filter((k) => ALLOWED_SCOPE_KEYS.includes(k))

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        minWidth: 420,
      }}
    >
      <FormControl size="small" sx={{ minWidth: 170 }}>
        <InputLabel id="scope-key-label" shrink>
          {labelKey}
        </InputLabel>
        <Select labelId="scope-key-label" label={labelKey} value={keySel} onChange={onKeyChange} displayEmpty notched>
          <MenuItem value="">&nbsp;</MenuItem>
          {keyOptions.map((k) => (
            <MenuItem key={k} value={k}>
              {k}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size="small"
        label={labelValue}
        value={val}
        onChange={(e) => onValueChange(e.target.value)}
        disabled={!keySel}
        inputProps={{ maxLength: 64 }}
        sx={{ minWidth: 220 }}
      />
    </Box>
  )
}

export default ScopeFieldEditor
